<script setup>
/**
 * TOOLS-05 — "Adicionar ferramenta" vira UM caminho, em etapas, dentro da mesma caixa.
 *
 * Por que existe. A seção tinha três abas (Tools, Variables, Legacy HTTP tools) e quatro fileiras
 * de filtro — Effect, Shape, Origin, State. Todas dividiam por vocabulário de sistema: quem opera
 * pergunta "o que este agente sabe fazer?" e "como eu dou uma capacidade nova a ele?", e nenhuma
 * das duas se responde com "qual sistema construiu essa tool". A aba de legado ainda dizia, por
 * escrito, que não se criava tool ali — logo acima de dois botões que criavam.
 *
 * O padrão é o do `powerData/components/AddSourceDialog.vue`: um `etapa` que troca dentro do mesmo
 * diálogo, título que acompanha, seta para voltar e pill de "Em breve" no que ainda não existe.
 * Aqui são cinco degraus em vez de dois, e o sexto (criar/editar) é outro diálogo, porque
 * preencher formulário não é navegar.
 *
 * As categorias são uma lista DAQUI, não do banco: não existe coluna de categoria, e os eixos que
 * existiriam sem migração (builtin×custom, native×declared, global×da conta) são justamente os que
 * não ajudam ninguém a decidir. Provedor que a conta tem vem das instâncias; provedor sem
 * instância não aparece — a conta não cria instância, isso é do provisionador.
 */
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import toolInstancesApi from 'dashboard/api/toolInstances';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import IconeDaTool from './IconeDaTool.vue';
import { mensagemDoErro } from '../erros';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
  // O que ainda pode ser ligado neste agente. Vem do `disponiveis` do tool_bindings.
  disponiveis: { type: Array, default: () => [] },
  // As instâncias de template da conta — o "provedor" da etapa 3.
  instancias: { type: Array, default: () => [] },
});
const emit = defineEmits(['attach', 'criar', 'instanciasMudaram']);

const { t } = useI18n();

const dialogRef = ref(null);
const etapa = ref('origem');
const categoria = ref(null);
const instancia = ref(null);
const marcadas = ref([]);
const segredos = ref({});
const salvando = ref(false);

// ── categorias ────────────────────────────────────────────────────────────────
// `soon: true` não é enfeite: é como o PowerData já anuncia o que vem, e não custa backend —
// a grade cheia é o que torna esta etapa legível enquanto há um provedor só de verdade.
const CATEGORIA_POR_TEMPLATE = {
  vtex: 'ecommerce',
  shopify: 'ecommerce',
  nuvemshop: 'ecommerce',
  tray: 'ecommerce',
};
const CATEGORIA_DE_FALLBACK = 'other';

function categoriaDaInstancia(inst) {
  return (
    CATEGORIA_POR_TEMPLATE[String(inst?.template || '').toLowerCase()] ||
    CATEGORIA_DE_FALLBACK
  );
}

const naoTemplate = computed(() =>
  props.disponiveis.filter(d => d.kind !== 'template')
);
const daPlataforma = computed(() => naoTemplate.value.filter(d => d.global));
const soDaConta = computed(() => naoTemplate.value.filter(d => !d.global));

// Quantas tools ainda ligáveis cada instância tem. Instância sem tool livre continua aparecendo:
// o cliente pode precisar entrar nela para configurar a credencial.
function toolsDaInstancia(id) {
  return props.disponiveis.filter(
    d => d.kind === 'template' && d.instance_id === id
  );
}

const categorias = computed(() => {
  const comProvedor = new Set(props.instancias.map(categoriaDaInstancia));
  const fixas = [
    {
      key: 'powerbizz',
      icon: 'i-lucide-zap',
      itens: daPlataforma.value.length,
    },
    { key: 'own', icon: 'i-lucide-building-2', itens: soDaConta.value.length },
    { key: 'ecommerce', icon: 'i-lucide-shopping-cart', provedor: true },
    { key: 'other', icon: 'i-lucide-plug', provedor: true },
    { key: 'crm', icon: 'i-lucide-users', soon: true },
    { key: 'logistics', icon: 'i-lucide-package', soon: true },
    { key: 'finance', icon: 'i-lucide-credit-card', soon: true },
    { key: 'erp', icon: 'i-lucide-boxes', soon: true },
  ];
  return (
    fixas
      // Categoria de provedor só existe se a conta tiver provedor nela; "Só sua" só se houver tool
      // dela. Categoria vazia na tela é promessa que a pessoa clica e não encontra nada.
      .filter(
        c => c.soon || (c.provedor ? comProvedor.has(c.key) : c.itens > 0)
      )
      .map(c => ({
        ...c,
        label: t(`POWER_AGENTS.TOOLS.ADD_CATEGORY_${c.key.toUpperCase()}`),
        hint: t(`POWER_AGENTS.TOOLS.ADD_CATEGORY_${c.key.toUpperCase()}_HINT`),
      }))
  );
});

const provedoresDaCategoria = computed(() =>
  props.instancias.filter(i => categoriaDaInstancia(i) === categoria.value)
);

// Tools oferecidas no degrau 5: as do provedor, ou as da categoria quando ela não é de provedor.
const ofertaAtual = computed(() => {
  if (instancia.value) return toolsDaInstancia(instancia.value.id);
  if (categoria.value === 'powerbizz') return daPlataforma.value;
  if (categoria.value === 'own') return soDaConta.value;
  return [];
});

// ── credencial ────────────────────────────────────────────────────────────────
// A conta NÃO cria instância (isso é do provisionador): aqui só se preenche o segredo, pelo
// `PATCH tool_instances/:id/secrets`, que é o único caminho de conta que existe.
const camposSecretos = computed(() => instancia.value?.campos_secretos || []);
const jaConfigurados = computed(() => instancia.value?.secrets || {});
const instanciaPronta = inst =>
  (inst?.campos_secretos || []).every(campo => (inst?.secrets || {})[campo]);

const podeSalvarSegredo = computed(() =>
  camposSecretos.value.some(campo => (segredos.value[campo] || '').trim())
);

function iniciais(nome) {
  const limpo = String(nome || '?').replace(/[^a-z0-9]/gi, '');
  return (limpo.slice(0, 2) || '?').toUpperCase();
}

function rotuloDoCampo(campo) {
  return campo.replace(/_/g, ' ');
}

// ── navegação ─────────────────────────────────────────────────────────────────
const TITULOS = {
  origem: () => t('POWER_AGENTS.TOOLS.ADD_TITLE'),
  categoria: () => t('POWER_AGENTS.TOOLS.ADD_FROM_CATALOG'),
  provedor: () =>
    categorias.value.find(c => c.key === categoria.value)?.label || '',
  credencial: () =>
    t('POWER_AGENTS.TOOLS.ADD_CONNECT_TITLE', {
      provider: instancia.value?.template || '',
    }),
  tools: () =>
    instancia.value?.template ||
    categorias.value.find(c => c.key === categoria.value)?.label ||
    '',
};
const titulo = computed(() => TITULOS[etapa.value]());
const podeVoltar = computed(() => etapa.value !== 'origem');

function escolherCategoria(c) {
  if (c.soon) return;
  categoria.value = c.key;
  // Categoria sem provedor (plataforma, ou as da própria conta) pula direto para as tools: não há
  // fornecedor a escolher, e um degrau com um card só é pedágio.
  etapa.value = c.provedor ? 'provedor' : 'tools';
  marcadas.value = [];
}

function escolherProvedor(inst) {
  instancia.value = inst;
  segredos.value = {};
  marcadas.value = [];
  // Sem credencial a tool não responde. Mandar para a lista de tools primeiro faria o cliente
  // ligar quatro ferramentas que falham na primeira conversa.
  etapa.value = instanciaPronta(inst) ? 'tools' : 'credencial';
}

function voltar() {
  if (etapa.value === 'tools' && instancia.value) etapa.value = 'provedor';
  else if (etapa.value === 'tools') etapa.value = 'categoria';
  else if (etapa.value === 'credencial') etapa.value = 'provedor';
  else if (etapa.value === 'provedor') etapa.value = 'categoria';
  else etapa.value = 'origem';

  if (etapa.value !== 'tools') marcadas.value = [];
  if (etapa.value === 'categoria') instancia.value = null;
}

function alternar(id) {
  const i = marcadas.value.indexOf(id);
  if (i >= 0) marcadas.value.splice(i, 1);
  else marcadas.value.push(id);
}

async function salvarCredencial() {
  salvando.value = true;
  try {
    const sujos = Object.fromEntries(
      Object.entries(segredos.value).filter(([, v]) => (v || '').trim())
    );
    const atualizada = await toolInstancesApi.updateSecrets(
      props.accountId,
      instancia.value.id,
      sujos
    );
    instancia.value = atualizada;
    segredos.value = {};
    emit('instanciasMudaram');
    useAlert(t('POWER_AGENTS.TOOLS.ADD_CONNECT_SAVED'));
    etapa.value = 'tools';
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.TOOLS.ERROR')));
  } finally {
    salvando.value = false;
  }
}

function confirmarTools() {
  if (!marcadas.value.length) return;
  emit('attach', [...marcadas.value]);
  marcadas.value = [];
}

function reset() {
  etapa.value = 'origem';
  categoria.value = null;
  instancia.value = null;
  marcadas.value = [];
  segredos.value = {};
}

// Quando o pai recarrega `disponiveis` depois de anexar, a oferta encolhe. Se acabou, não faz
// sentido continuar num degrau vazio.
watch(
  () => props.disponiveis,
  () => {
    if (etapa.value === 'tools' && !ofertaAtual.value.length)
      etapa.value = 'origem';
  }
);

defineExpose({
  open: () => {
    reset();
    dialogRef.value?.open();
  },
  close: () => dialogRef.value?.close(),
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="titulo"
    :width="etapa === 'credencial' ? 'lg' : '2xl'"
    :show-confirm-button="false"
    :show-cancel-button="false"
    overflow-y-auto
    @close="reset"
  >
    <div class="flex flex-col gap-3">
      <button
        v-if="podeVoltar"
        type="button"
        class="flex items-center gap-1.5 text-xs w-fit text-n-slate-11 hover:text-n-slate-12"
        @click="voltar"
      >
        <i class="i-lucide-arrow-left" />
        {{ t('POWER_AGENTS.TOOLS.ADD_BACK') }}
      </button>

      <!-- 1 · de onde vem -->
      <div v-if="etapa === 'origem'" class="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          class="flex flex-col items-start gap-1 p-4 text-start transition-colors border rounded-lg border-n-weak hover:border-n-brand hover:bg-n-alpha-1"
          @click="etapa = 'categoria'"
        >
          <div class="flex items-center w-full gap-2">
            <i class="i-lucide-library text-n-slate-11" />
            <span class="text-body-main text-n-slate-12">
              {{ t('POWER_AGENTS.TOOLS.ADD_FROM_CATALOG') }}
            </span>
          </div>
          <span class="text-body-small text-n-slate-11">
            {{ t('POWER_AGENTS.TOOLS.ADD_FROM_CATALOG_HINT') }}
          </span>
        </button>

        <button
          type="button"
          class="flex flex-col items-start gap-1 p-4 text-start transition-colors border rounded-lg border-n-weak hover:border-n-brand hover:bg-n-alpha-1"
          data-ferramenta-http
          @click="emit('criar')"
        >
          <!-- FLOW-13 (S4): o "Criar do zero" virou a "Ferramenta HTTP", que abre a tela dela. -->
          <div class="flex items-center w-full gap-2">
            <i class="i-lucide-globe text-n-slate-11" />
            <span class="text-body-main text-n-slate-12">
              {{ t('POWER_AGENTS.TOOLS.ADD_CUSTOM') }}
            </span>
          </div>
          <span class="text-body-small text-n-slate-11">
            {{ t('POWER_AGENTS.TOOLS.ADD_CUSTOM_HINT') }}
          </span>
        </button>
      </div>

      <!-- 2 · categorias -->
      <div v-else-if="etapa === 'categoria'" class="grid gap-3 sm:grid-cols-2">
        <button
          v-for="c in categorias"
          :key="c.key"
          type="button"
          class="flex flex-col items-start gap-1 p-4 text-start transition-colors border rounded-lg"
          :class="
            c.soon
              ? 'border-n-weak bg-n-alpha-1 cursor-not-allowed opacity-60'
              : 'border-n-weak hover:border-n-brand hover:bg-n-alpha-1'
          "
          :disabled="c.soon"
          @click="escolherCategoria(c)"
        >
          <div class="flex items-center w-full gap-2">
            <i :class="c.icon" class="text-n-slate-11" />
            <span class="text-body-main text-n-slate-12">{{ c.label }}</span>
            <span
              v-if="c.soon"
              class="ltr:ml-auto rtl:mr-auto px-1.5 py-0.5 rounded text-body-small bg-n-alpha-2 text-n-slate-11"
            >
              {{ t('POWER_AGENTS.TOOLS.ADD_SOON') }}
            </span>
          </div>
          <span class="text-body-small text-n-slate-11">{{ c.hint }}</span>
        </button>
      </div>

      <!-- 3 · provedores -->
      <div v-else-if="etapa === 'provedor'" class="flex flex-col gap-3">
        <p
          v-if="!provedoresDaCategoria.length"
          class="text-body-small text-n-slate-11"
        >
          {{ t('POWER_AGENTS.TOOLS.ADD_PROVIDER_EMPTY') }}
        </p>
        <div v-else class="grid gap-3 sm:grid-cols-2">
          <button
            v-for="inst in provedoresDaCategoria"
            :key="inst.id"
            type="button"
            class="flex flex-col items-start gap-1 p-4 text-start transition-colors border rounded-lg border-n-weak hover:border-n-brand hover:bg-n-alpha-1"
            @click="escolherProvedor(inst)"
          >
            <div class="flex items-center w-full gap-2">
              <!-- Monograma NEUTRO, no mesmo tom do ícone. A coleção `logos` não tem marca para
                   os provedores que temos, e misturar um logo colorido com iniciais chamaria
                   atenção para a inconsistência em vez de para a marca. -->
              <span
                class="grid w-6 h-6 text-xs font-semibold border rounded place-items-center border-n-weak bg-n-alpha-2 text-n-slate-11"
              >
                {{ iniciais(inst.template) }}
              </span>
              <span class="text-body-main text-n-slate-12">{{
                inst.template
              }}</span>
              <!-- Estado é glifo colorido com texto neutro: etiqueta de fundo colorido não se usa
                   no OS (Murilo, 16/10). -->
              <span
                class="inline-flex items-center gap-1.5 text-xs font-medium ltr:ml-auto rtl:mr-auto text-n-slate-11"
                data-estado-do-provedor
              >
                <span
                  class="rounded-full size-2"
                  :class="
                    instanciaPronta(inst) ? 'bg-n-teal-9' : 'bg-n-amber-9'
                  "
                  aria-hidden="true"
                />
                {{
                  instanciaPronta(inst)
                    ? t('POWER_AGENTS.TOOLS.ADD_PROVIDER_CONNECTED')
                    : t('POWER_AGENTS.TOOLS.ADD_PROVIDER_CONFIGURE')
                }}
              </span>
            </div>
            <span class="text-body-small text-n-slate-11">
              {{
                instanciaPronta(inst)
                  ? t(
                      'POWER_AGENTS.TOOLS.ADD_PROVIDER_TOOLS',
                      { count: toolsDaInstancia(inst.id).length },
                      toolsDaInstancia(inst.id).length
                    )
                  : t('POWER_AGENTS.TOOLS.ADD_PROVIDER_NEEDS_KEY')
              }}
            </span>
          </button>
        </div>
      </div>

      <!-- 4 · credencial do provedor -->
      <div v-else-if="etapa === 'credencial'" class="flex flex-col gap-3">
        <div
          v-for="campo in camposSecretos"
          :key="campo"
          class="flex flex-col gap-1"
        >
          <label class="text-xs capitalize text-n-slate-11">{{
            rotuloDoCampo(campo)
          }}</label>
          <input
            v-model="segredos[campo]"
            type="password"
            autocomplete="new-password"
            :placeholder="
              jaConfigurados[campo]
                ? t('POWER_AGENTS.TOOLS.ADD_CONNECT_KEEP')
                : ''
            "
            class="w-full px-3 py-2 font-mono text-sm border rounded-lg outline-none reset-base border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
          />
        </div>
        <p class="text-body-small text-n-slate-11">
          {{ t('POWER_AGENTS.TOOLS.ADD_CONNECT_NOTE') }}
        </p>
        <Button
          size="sm"
          :label="t('POWER_AGENTS.TOOLS.ADD_CONNECT_SAVE')"
          :is-loading="salvando"
          :disabled="!podeSalvarSegredo"
          class="self-start"
          @click="salvarCredencial"
        />
      </div>

      <!-- 5 · as ferramentas -->
      <div v-else-if="etapa === 'tools'" class="flex flex-col gap-2">
        <p class="text-xs font-medium tracking-wide uppercase text-n-slate-11">
          {{ t('POWER_AGENTS.TOOLS.ADD_PICK_TOOLS') }}
        </p>
        <p v-if="!ofertaAtual.length" class="text-body-small text-n-slate-11">
          {{ t('POWER_AGENTS.TOOLS.ADD_PICK_NONE') }}
        </p>
        <button
          v-for="tool in ofertaAtual"
          :key="tool.id"
          type="button"
          class="flex items-center gap-3 px-3 py-2.5 text-start transition-colors border rounded-lg border-n-weak hover:border-n-brand"
          @click="alternar(tool.id)"
        >
          <!-- O mesmo ícone das cartas da aba Ferramentas: blocos ligados para flow, chave para o resto.
               FLOW-13 (S4): o globo na ferramenta HTTP, pela `forma` que o Rails manda em `disponiveis`. -->
          <IconeDaTool
            :flow="tool.kind === 'flow'"
            :http="tool.forma === 'ferramenta_http'"
          />
          <span class="flex flex-col min-w-0 grow">
            <span class="text-sm font-medium truncate text-n-slate-12">{{
              tool.name
            }}</span>
            <span class="text-xs text-n-slate-11">{{ tool.description }}</span>
          </span>
          <span
            class="grid rounded size-4 place-items-center shrink-0 border"
            :class="
              marcadas.includes(tool.id)
                ? 'bg-n-brand border-n-brand text-white'
                : 'border-n-weak text-transparent'
            "
          >
            <i class="text-xs i-lucide-check" />
          </span>
        </button>
        <Button
          v-if="ofertaAtual.length"
          size="sm"
          icon="i-lucide-plus"
          :label="
            t(
              'POWER_AGENTS.TOOLS.ADD_PICK_SUBMIT',
              { count: marcadas.length },
              marcadas.length
            )
          "
          :disabled="!marcadas.length"
          class="self-start mt-1"
          @click="confirmarTools"
        />
      </div>
    </div>
  </Dialog>
</template>
